import { getFirestore, getDoc } from "firebase/firestore"
import { doc } from "firebase/firestore"
import { getActiveInspection } from "./Inspection"

import db, { getMedia, MediaType, ensureDb } from "@/database"

export type FourPointType = {
  electrical: {
    images?: string[] | MediaType[]
    wiringTypeCopper?: boolean
    wiringTypeConduit?: boolean
    clothWiring?: boolean
    activeKnobTube?: boolean
    copalum?: boolean
    alumiConn?: boolean
    branchCircuit?: boolean
    blowingFuses?: boolean
    trippingBreakers?: boolean
    emptySockets?: boolean
    looseWiring?: boolean
    improperGrounding?: boolean
    corrosion?: boolean
    overFusing?: boolean
    doubleTaps?: boolean
    exposedWiring?: boolean
    unsafeWiring?: boolean
    improperBreakerSize?: boolean
    scorching?: boolean
    hazards?: string
    mainPanel?: string
    mainPanelTotalAmps?: string
    mainPanelAge?: string
    mainPanelYearLastUpdated?: string
    mainPanelBrandModel?: string
    mainPanelamperageSufficient?: string
    mainPanelAmperageSufficientExplained?: string
    secondaryPanel?: string
    secondaryPanelTotalAmps?: string
    secondaryPanelAge?: string
    secondaryPanelYearLastUpdated?: string
    secondaryPanelBrandModel?: string
    secondaryPanelAmperageSufficient?: string
    secondaryPanelAmperageSufficientExplained?: string
    branchCircuitDetails?: string
    condition?: string
    unsatisfactoryExplain?: string
  }
  hvac: {
    images?: string[] | MediaType[]
    age?: string
    yearLastUpdated?: string
    centralAC?: string
    centralHeat?: string
    primaryHeatSource?: string
    condition?: string
    conditionExplained?: string
    serviceInspectionDate?: string
    stoveProfessionallyInstall?: string
    spaceHeater?: string
    heatSourcePortable?: string
    condensateDamage?: string
  }
  plumbing: {
    images?: string[] | MediaType[]
    originalHome?: string
    completelyRepiped?: string
    partiallyRepiped?: string
    renovationComment?: string
    otherPipeType?: string
    pressureReliefValve?: string
    priorLeak?: string
    activeLeak?: string
    waterHeaterLocation?: string
    waterHeaterAge?: string
    dashwasher?: string
    refrigerator?: string
    washingMachine?: string
    waterHeater?: string
    showerTubs?: string
    toilets?: string
    sinks?: string
    sumppump?: string
    mainShutoffValve?: string
    allOtherVisible?: string
    unsatisfactoryExplained?: string
    copperPipe?: boolean
    pvcPipe?: boolean
    galvanizedPipe?: boolean
    PEXPipe?: boolean
    polybutylenePipe?: boolean
  }
  roof: {
    images?: string[] | MediaType[]
    coveringMaterial?: string
    age?: string
    remainingLife?: string
    lastPermit?: string
    lastUpdate?: string
    percentageReplacement?: string
    condition?: string
    conditionExplained?: string
    signsOfLeaks?: string
    atticDecking?: string
    interiorCeilings?: string
    secondaryCoveringMaterial?: string
    secondaryAge?: string
    secondaryRemainingLife?: string
    secondaryLastPermit?: string
    secondaryPercentageReplacement?: string
    secondaryConditionExplained?: string
    secondaryCondition?: string
    secondaryAtticDecking?: string
    secondaryInteriorCeilings?: string
    secondarySignsOfLeaks?: string
    fullReplacement?: boolean
    partialReplacement?: boolean
    cracking?: boolean
    cuppingCurling?: boolean
    granuleLoss?: boolean
    exposedAsphalt?: boolean
    exposedFelt?: boolean
    crackedTabs?: boolean
    softDecking?: boolean
    hailDamage?: boolean
    secondaryFullReplacement?: boolean
    secondaryPartialReplacement?: boolean
    secondaryCracking?: boolean
    secondaryCuppingCurling?: boolean
    secondaryGranuleLoss?: boolean
    secondaryExposedAsphalt?: boolean
    secondaryExposedFelt?: boolean
    secondaryCrackedTabs?: boolean
    secondarySoftDecking?: boolean
    secondaryHailDamage?: boolean
  }
  dwelling: { images?: string[] | MediaType[] }
  applicationPolicy?: string
  yearBuilt?: string
  additionalComments?: string
}
export async function getFourPoint() {
  const id = getActiveInspection()
  await ensureDb()
  // 1. Serve from local Dexie cache immediately (offline-first)
  const local = await db.table("fourpoints").get(id) as FourPointType | undefined
  if (local) return local

  // 2. Fetch from Firestore and cache locally for next time
  const snapshot = await getDoc(doc(getFirestore(), `fourPoints/${id}`))

  if (!snapshot.exists()) {
    return { electrical: {}, hvac: {}, plumbing: {}, dwelling: {}, roof: {} }
  }

  const data = snapshot.data() as FourPointType
  await db.table("fourpoints").put({ ...data, id }).catch(() => { })
  return data
}
export async function getFormattedFourPoint(): Promise<FourPointType> {
  const { electrical, hvac, dwelling, plumbing, roof } = await getFourPoint()
  const fetchImages = async (images: string[]) => {
    let results = []
    for (let i = 0; i < images.length; i++) {
      const image = await getMedia(images[i])
      if (!image) continue
      if (image?.dataURI) delete image.dataURI
      results.push(image)
    }
    return results
  }
  if (electrical?.images) {
    let images = await fetchImages(electrical?.images as string[])
    electrical.images = images
  }
  if (hvac?.images) {
    let images = await fetchImages(hvac.images as string[])
    hvac.images = images
  }
  if (dwelling?.images) {
    let images = await fetchImages(dwelling.images as string[])
    dwelling.images = images
  }
  if (plumbing?.images) {
    let images = await fetchImages(plumbing.images as string[])
    plumbing.images = images
  }
  if (roof?.images) {
    let images = await fetchImages(roof.images as string[])
    roof.images = images
  }

  return { electrical, hvac, dwelling, plumbing, roof }
}
